import { parseISO } from 'date-fns';
import { Size } from '@Components/designConstants';
import { AlertFormData, BudgetOption } from '../anomalyDetectionConstants';

// Shared prop bundle for the section-header <Checkbox> instances in AlertForm and
// PresetForm (18×18 input via size='large', with the `.section-checkbox` layout hook).
export const SECTION_CHECKBOX_PROPS = {
  size: Size.LG,
  className: 'section-checkbox'
} as const;

export type FrequencyType = 'hourly' | 'daily' | 'weekly' | 'monthly';
export type ThresholdType = 'percentage' | 'dollar';

export interface AlertChannel {
  type: string;
  people?: string[];
  channels?: string[];
  recipients?: string[];
}

export interface AlertParameters {
  thresholdAbove?: number;
  thresholdBelow?: number;
  thresholdType?: ThresholdType;
  budgetId?: number;
  budgetType?: 'total' | 'ideal';
  alwaysNotify?: boolean;
  spendBehaviorModel?: string;
  customSQL?: string;
}

export interface AlertRow {
  id: string;
  alertGroupId: string;
  alertName: string;
  company: string;
  alertType: string;
  checkType: string;
  parameters: AlertParameters;
  alertChannels: AlertChannel[];
  startDate: string;
  endDate: string;
  isEnabled: boolean;
  isDeleted: boolean;
  groupIsActive: boolean;
  groupIsDeleted: boolean;
  createdByEmail: string;
  dateCreated: string;
  lastModified: string;
  frequencyType: FrequencyType;
  frequencyTime: string;
  frequencyDayOfWeek: string | null;
  frequencyDayOfMonth: number | null;
  lastModifiedByEmail: string | null;
}

export interface DatabaseAlert {
  alert_name: string;
  company: string;
  alert_type: string;
  check_type: string;
  parameters: Record<string, unknown>;
  alert_channels: AlertChannel[];
  frequency_type: FrequencyType;
  frequency_time: string;
  frequency_day_of_week: string | null;
  frequency_day_of_month: number | null;
  start_date: string;
  end_date: string;
  is_enabled: boolean;
  group_is_active: boolean;
}

const MINUTES_PER_HOUR = 60;
const HOURS_PER_HALF_DAY = 12;

/**
 * Sorts items by their `lastModified` timestamp, newest first. Returns a new
 * array (does not mutate the input).
 */
export const sortByLastModifiedDesc = <T extends { lastModified: string }>(items: T[]): T[] =>
  [...items].sort(
    (a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
  );

/**
 * Formats an ISO date string as a relative "time ago" label (e.g. "5 minutes ago").
 */
export const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffInMinutes < 1) {
    return 'Just now';
  }
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
};

/**
 * Returns the browser's current UTC offset, in minutes, that should be added to
 * a local wall-clock time to obtain UTC (e.g. UTC-5 returns 300).
 */
const getUtcOffsetMinutes = (): number => new Date().getTimezoneOffset();

const pad = (value: number): string => value.toString().padStart(2, '0');

/**
 * Converts a user time string (e.g. "2:30pm" or "10:15am") to a UTC time string
 * in "HH:mm:ss" format, based on the browser's timezone.
 */
export const convertUserTimeToUTC = (timeStr: string): string => {
  const time = timeStr.toLowerCase();
  const [rawHours, rawMinutes] = time.replace(/[amp]/g, '').split(':');
  let hour = parseInt(rawHours, 10);
  const minute = parseInt(rawMinutes, 10) || 0;

  if (time.includes('pm') && hour !== HOURS_PER_HALF_DAY) {
    hour += HOURS_PER_HALF_DAY;
  }
  if (time.includes('am') && hour === HOURS_PER_HALF_DAY) {
    hour = 0;
  }

  const totalMinutesUtc =
    (hour * MINUTES_PER_HOUR + minute + getUtcOffsetMinutes() + MINUTES_PER_HOUR * 24) %
    (MINUTES_PER_HOUR * 24);
  const utcHour = Math.floor(totalMinutesUtc / MINUTES_PER_HOUR);
  const utcMinute = totalMinutesUtc % MINUTES_PER_HOUR;

  return `${pad(utcHour)}:${pad(utcMinute)}:00`;
};

/**
 * Converts a UTC time string ("HH:mm:ss" or "HH:mm") to a user time string
 * (e.g. "2:30pm"), based on the browser's timezone.
 */
export const convertUTCToUserTime = (utcTimeStr: string): string => {
  const [rawHours, rawMinutes] = utcTimeStr.split(':');
  const hour = parseInt(rawHours, 10);
  const minute = parseInt(rawMinutes, 10) || 0;

  const totalMinutesLocal =
    (hour * MINUTES_PER_HOUR + minute - getUtcOffsetMinutes() + MINUTES_PER_HOUR * 24) %
    (MINUTES_PER_HOUR * 24);
  const localHour = Math.floor(totalMinutesLocal / MINUTES_PER_HOUR);
  const localMinute = totalMinutesLocal % MINUTES_PER_HOUR;

  const meridiem = localHour < HOURS_PER_HALF_DAY ? 'am' : 'pm';
  const displayHour = localHour % HOURS_PER_HALF_DAY || HOURS_PER_HALF_DAY;
  const minuteSuffix = pad(localMinute);

  return `${displayHour}:${minuteSuffix}${meridiem}`;
};

export const hasValidNonEmptyStrings = (array: string[]): boolean =>
  array.length > 0 && array.some(item => item && item.trim() !== '');

/**
 * The option value equal to `value`, or null when no option has it. A Dropdown shows a value
 * that no option has as raw text, so this keeps the placeholder for a stale or default value.
 */
export const matchOptionValue = <T extends string>(
  options: readonly { value: T }[],
  value: string | null | undefined
): T | null => options.find(option => option.value === value)?.value ?? null;

/** The option values that `values` holds, in option order. See matchOptionValue. */
export const matchOptionValues = <T extends string>(
  options: readonly { value: T }[],
  values: readonly string[]
): T[] => options.filter(option => values.includes(option.value)).map(option => option.value);

/**
 * The alerts API returns dates as either 'yyyy-MM-dd' or a full ISO timestamp
 * (e.g. '2026-07-16T00:00:00.000Z'). The DatePicker expects a plain
 * 'yyyy-MM-dd' string and throws on anything else, so extract the calendar-date
 * portion directly. Slicing (rather than parseISO + format) avoids shifting the
 * day across timezones, since the stored timestamps are UTC midnight.
 */
export const toDateInputValue = (value: string | null | undefined): string => {
  const match = value?.match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : '';
};

export const validateMonitoringType = (formData: AlertFormData): string | null => {
  const hasMonitoringType =
    formData.actual.enabled || formData.projected.enabled || formData.remainder.enabled;
  if (!hasMonitoringType) {
    return 'At least one monitoring type (Actual, Projected, or Remainder) must be enabled';
  }
  return null;
};

export const validateNotificationMethods = (formData: AlertFormData): Record<string, string> => {
  const fieldErrors: Record<string, string> = {};

  if (!formData.email.enabled && !formData.slack.enabled) {
    fieldErrors.notificationMethod =
      'At least one notification method (Slack or Email) must be enabled';
  }

  if (formData.slack.enabled) {
    const hasValidChannels = hasValidNonEmptyStrings(formData.slack.channels);
    const hasValidMembers = hasValidNonEmptyStrings(formData.slack.members);
    if (!hasValidChannels) {
      fieldErrors.slackChannels = 'At least one Slack channel is required';
    }
    if (!hasValidMembers) {
      fieldErrors.slackMembers = 'At least one Slack member is required';
    }
  }

  if (formData.email.enabled) {
    const hasValidAddresses = hasValidNonEmptyStrings(formData.email.addresses);
    if (!hasValidAddresses) {
      fieldErrors.emailAddresses = 'At least one email address is required';
    }
  }

  return fieldErrors;
};

// Settings set outside the form (e.g. notifyOn) survive an edit, but only from an existing custom SQL alert.
const buildCustomSqlParameters = (
  customSql: string,
  existing?: AlertParameters
): Record<string, unknown> => ({
  ...(existing?.customSQL ? existing : {}),
  customSQL: customSql
});

export const convertFormDataToDatabaseAlerts = (
  formData: AlertFormData,
  company: string,
  budgetOptions: BudgetOption[],
  email: string,
  groupIsActive?: boolean,
  existingParameters?: AlertParameters
): DatabaseAlert[] => {
  const selectedBudget = budgetOptions.find(opt => parseInt(opt.value, 10) === formData.budgetId);
  const budgetData = selectedBudget?.rowData;

  const startDate =
    formData.startDate || (budgetData?.start_date ? parseISO(budgetData.start_date) : new Date());

  let endDate;
  if (formData.alertType === 'custom' && !formData.endDate) {
    const farFuture = new Date();
    farFuture.setFullYear(farFuture.getFullYear() + 10);
    endDate = farFuture;
  } else {
    endDate =
      formData.endDate || (budgetData?.end_date ? parseISO(budgetData.end_date) : new Date());
  }

  const formatDate = (date: Date | string) => {
    if (typeof date === 'string') {
      return date;
    }
    return date.toISOString().split('T')[0];
  };

  const parseTime = (timeStr: string): string => convertUserTimeToUTC(timeStr);

  const alertChannels: AlertChannel[] = [];
  if (formData.slack.enabled && formData.slack.channels.length > 0) {
    alertChannels.push({
      type: 'slack',
      channels: formData.slack.channels.map(channel => `#${channel}`),
      people: formData.slack.members.map(memberId => `<@${memberId}>`)
    });
  }

  if (formData.email.enabled) {
    alertChannels.push({
      type: 'email',
      recipients: formData.email.addresses
    });
  }

  const alerts: DatabaseAlert[] = [];

  if (formData.alertType === 'custom') {
    const customSqlParams = buildCustomSqlParameters(formData.customSql, existingParameters);

    alerts.push({
      alert_name: formData.alertName,
      company,
      alert_type: 'custom_sql',
      check_type: 'custom',
      parameters: customSqlParams,
      alert_channels: alertChannels,
      frequency_type: formData.actual.frequency.type,
      frequency_time: parseTime(formData.actual.frequency.time),
      frequency_day_of_week:
        formData.actual.frequency.type === 'weekly' ? formData.actual.frequency.dayOfWeek : null,
      frequency_day_of_month:
        formData.actual.frequency.type === 'monthly'
          ? parseInt(formData.actual.frequency.dayOfMonth, 10)
          : null,
      start_date: formatDate(startDate),
      end_date: formatDate(endDate),
      is_enabled: true,
      group_is_active: groupIsActive ?? formData.groupIsActive ?? true
    });

    return alerts;
  }

  const actualParams = {
    budgetId: budgetData?.id ? parseInt(budgetData.id.toString(), 10) : formData.budgetId,
    thresholdAbove:
      formData.actual.thresholdType === 'percentage'
        ? parseFloat(formData.actual.over.replace(/[^0-9.]/g, '')) / 100
        : parseFloat(formData.actual.over.replace(/[^0-9.]/g, '')),
    thresholdBelow:
      formData.actual.thresholdType === 'percentage'
        ? parseFloat(formData.actual.under.replace(/[^0-9.]/g, '')) / 100
        : parseFloat(formData.actual.under.replace(/[^0-9.]/g, '')),
    thresholdType: formData.actual.thresholdType,
    budgetType: formData.actual.budgetType,
    alwaysNotify: formData.actual.alwaysNotify,
    spendBehaviorModel: formData.spendCadence === 'Evenly distributed' ? 'normal' : 'custom'
  };

  alerts.push({
    alert_name: formData.alertName,
    company,
    alert_type: 'budget',
    check_type: 'actual',
    parameters: actualParams,
    alert_channels: alertChannels,
    frequency_type: formData.actual.frequency.type,
    frequency_time: parseTime(formData.actual.frequency.time),
    frequency_day_of_week:
      formData.actual.frequency.type === 'weekly' ? formData.actual.frequency.dayOfWeek : null,
    frequency_day_of_month:
      formData.actual.frequency.type === 'monthly'
        ? parseInt(formData.actual.frequency.dayOfMonth, 10)
        : null,
    start_date: formatDate(startDate),
    end_date: formatDate(endDate),
    is_enabled: formData.actual.enabled,
    group_is_active: groupIsActive ?? formData.groupIsActive ?? true
  });

  const projectedParams = {
    budgetId: budgetData?.id ? parseInt(budgetData.id.toString(), 10) : formData.budgetId,
    thresholdAbove:
      formData.projected.thresholdType === 'percentage'
        ? parseFloat(formData.projected.over.replace(/[^0-9.]/g, '')) / 100
        : parseFloat(formData.projected.over.replace(/[^0-9.]/g, '')),
    thresholdBelow:
      formData.projected.thresholdType === 'percentage'
        ? parseFloat(formData.projected.under.replace(/[^0-9.]/g, '')) / 100
        : parseFloat(formData.projected.under.replace(/[^0-9.]/g, '')),
    thresholdType: formData.projected.thresholdType,
    alwaysNotify: formData.projected.alwaysNotify,
    spendBehaviorModel: formData.spendCadence === 'Evenly distributed' ? 'normal' : 'custom'
  };

  alerts.push({
    alert_name: formData.alertName,
    company,
    alert_type: 'budget',
    check_type: 'projected',
    parameters: projectedParams,
    alert_channels: alertChannels,
    frequency_type: formData.projected.frequency.type,
    frequency_time: parseTime(formData.projected.frequency.time),
    frequency_day_of_week:
      formData.projected.frequency.type === 'weekly'
        ? formData.projected.frequency.dayOfWeek
        : null,
    frequency_day_of_month:
      formData.projected.frequency.type === 'monthly'
        ? parseInt(formData.projected.frequency.dayOfMonth, 10)
        : null,
    start_date: formatDate(startDate),
    end_date: formatDate(endDate),
    is_enabled: formData.projected.enabled,
    group_is_active: groupIsActive ?? formData.groupIsActive ?? true
  });

  const remainderParams = {
    budgetId: budgetData?.id ? parseInt(budgetData.id.toString(), 10) : formData.budgetId,
    thresholdAbove:
      formData.remainder.thresholdType === 'percentage'
        ? parseFloat(formData.remainder.over.replace(/[^0-9.]/g, '')) / 100
        : parseFloat(formData.remainder.over.replace(/[^0-9.]/g, '')),
    thresholdBelow:
      formData.remainder.thresholdType === 'percentage'
        ? parseFloat(formData.remainder.under.replace(/[^0-9.]/g, '')) / 100
        : parseFloat(formData.remainder.under.replace(/[^0-9.]/g, '')),
    thresholdType: formData.remainder.thresholdType,
    alwaysNotify: formData.remainder.alwaysNotify,
    spendBehaviorModel: formData.spendCadence === 'Evenly distributed' ? 'normal' : 'custom'
  };

  alerts.push({
    alert_name: formData.alertName,
    company,
    alert_type: 'budget',
    check_type: 'remainder',
    parameters: remainderParams,
    alert_channels: alertChannels,
    frequency_type: formData.remainder.frequency.type,
    frequency_time: parseTime(formData.remainder.frequency.time),
    frequency_day_of_week:
      formData.remainder.frequency.type === 'weekly'
        ? formData.remainder.frequency.dayOfWeek
        : null,
    frequency_day_of_month:
      formData.remainder.frequency.type === 'monthly'
        ? parseInt(formData.remainder.frequency.dayOfMonth, 10)
        : null,
    start_date: formatDate(startDate),
    end_date: formatDate(endDate),
    is_enabled: formData.remainder.enabled,
    group_is_active: groupIsActive ?? formData.groupIsActive ?? true
  });

  return alerts;
};

export interface GroupedAlert {
  alertGroupId: string;
  alertName: string;
  company: string;
  alertType: string;
  checkTypes: string[];
  alertChannels: AlertChannel[];
  startDate: string;
  endDate: string;
  groupIsActive: boolean;
  groupIsDeleted: boolean;
  createdByEmail: string;
  dateCreated: string;
  lastModified: string;
  lastModifiedByEmail: string | null;
  individualAlerts: AlertRow[];
}

export const mapGroupedAlertToFormData = (groupedAlert: GroupedAlert): AlertFormData | null => {
  if (
    !groupedAlert ||
    !groupedAlert.individualAlerts ||
    groupedAlert.individualAlerts.length === 0
  ) {
    return null;
  }

  const firstAlert = groupedAlert.individualAlerts[0];

  const slackMembers =
    groupedAlert.alertChannels
      ?.find(channel => channel.type === 'slack')
      ?.people?.map(person => person.replace(/[<>@]/g, '')) || [];

  const slackChannels =
    groupedAlert.alertChannels
      ?.find(channel => channel.type === 'slack')
      ?.channels?.map(channel => channel.replace('#', ''))?.[0] || '';

  const emailAddresses =
    groupedAlert.alertChannels?.find(channel => channel.type === 'email')?.recipients || [];

  const isCustomSqlAlert =
    firstAlert.alertType === 'custom_sql' && firstAlert.checkType === 'custom';
  const actualAlert = groupedAlert.individualAlerts.find(alert => alert.checkType === 'actual');
  const projectedAlert = groupedAlert.individualAlerts.find(
    alert => alert.checkType === 'projected'
  );
  const remainderAlert = groupedAlert.individualAlerts.find(
    alert => alert.checkType === 'remainder'
  );

  const actualEnabled = actualAlert?.isEnabled || false;
  const projectedEnabled = projectedAlert?.isEnabled || false;
  const remainderEnabled = remainderAlert?.isEnabled || false;

  const toThresholdString = (value: number | undefined, thresholdType: string): string => {
    if (value === undefined || value === null) {
      return thresholdType === 'dollar' ? '$20' : '20%';
    }

    if (thresholdType === 'dollar') {
      return `$${Math.round(value)}`;
    }
    return `${Math.round(value * 100)}%`;
  };

  let spendCadence = 'Custom';
  if (isCustomSqlAlert || firstAlert.parameters?.spendBehaviorModel === 'normal') {
    spendCadence = 'Evenly distributed';
  }

  return {
    alertName: groupedAlert.alertName || '',
    alertType: isCustomSqlAlert ? 'custom' : 'budget',
    budgetId: isCustomSqlAlert ? 0 : firstAlert.parameters?.budgetId || 0,
    spendCadence,
    customSql: isCustomSqlAlert ? firstAlert.parameters?.customSQL || '' : '',
    actual: {
      enabled: isCustomSqlAlert ? true : actualEnabled,
      over: isCustomSqlAlert
        ? '20%'
        : toThresholdString(
            actualAlert?.parameters?.thresholdAbove,
            actualAlert?.parameters?.thresholdType || 'percentage'
          ),
      under: isCustomSqlAlert
        ? '20%'
        : toThresholdString(
            actualAlert?.parameters?.thresholdBelow,
            actualAlert?.parameters?.thresholdType || 'percentage'
          ),
      thresholdType: actualAlert?.parameters?.thresholdType || 'percentage',
      budgetType: actualAlert?.parameters?.budgetType || 'total',
      alwaysNotify: actualAlert?.parameters?.alwaysNotify || false,
      frequency: {
        type: firstAlert?.frequencyType || 'daily',
        time: convertUTCToUserTime(firstAlert?.frequencyTime || '10:00:00'),
        dayOfWeek: firstAlert?.frequencyDayOfWeek || 'Monday',
        dayOfMonth: firstAlert?.frequencyDayOfMonth?.toString() || '1'
      }
    },
    projected: {
      enabled: isCustomSqlAlert ? false : projectedEnabled,
      over: isCustomSqlAlert
        ? '20%'
        : toThresholdString(
            projectedAlert?.parameters?.thresholdAbove,
            projectedAlert?.parameters?.thresholdType || 'percentage'
          ),
      under: isCustomSqlAlert
        ? '20%'
        : toThresholdString(
            projectedAlert?.parameters?.thresholdBelow,
            projectedAlert?.parameters?.thresholdType || 'percentage'
          ),
      thresholdType: projectedAlert?.parameters?.thresholdType || 'percentage',
      alwaysNotify: projectedAlert?.parameters?.alwaysNotify || false,
      frequency: {
        type: projectedAlert?.frequencyType || 'daily',
        time: convertUTCToUserTime(projectedAlert?.frequencyTime || '10:00:00'),
        dayOfWeek: projectedAlert?.frequencyDayOfWeek || 'Monday',
        dayOfMonth: projectedAlert?.frequencyDayOfMonth?.toString() || '1'
      }
    },
    remainder: {
      enabled: isCustomSqlAlert ? false : remainderEnabled,
      under: isCustomSqlAlert
        ? '20%'
        : toThresholdString(
            remainderAlert?.parameters?.thresholdBelow,
            remainderAlert?.parameters?.thresholdType || 'percentage'
          ),
      over: isCustomSqlAlert
        ? '20%'
        : toThresholdString(
            remainderAlert?.parameters?.thresholdAbove,
            remainderAlert?.parameters?.thresholdType || 'percentage'
          ),
      thresholdType: remainderAlert?.parameters?.thresholdType || 'percentage',
      alwaysNotify: remainderAlert?.parameters?.alwaysNotify || false,
      frequency: {
        type: remainderAlert?.frequencyType || 'daily',
        time: convertUTCToUserTime(remainderAlert?.frequencyTime || '10:00:00'),
        dayOfWeek: remainderAlert?.frequencyDayOfWeek || 'Monday',
        dayOfMonth: remainderAlert?.frequencyDayOfMonth?.toString() || '1'
      }
    },
    slack: {
      enabled: slackMembers.length > 0,
      channels: slackChannels ? [slackChannels] : [],
      members: slackMembers
    },
    email: {
      enabled: emailAddresses.length > 0,
      addresses: emailAddresses
    },
    startDate: toDateInputValue(groupedAlert.startDate),
    endDate: toDateInputValue(groupedAlert.endDate),
    groupIsActive: groupedAlert.groupIsActive ?? true
  };
};
